import type { CockpitTask } from "@/lib/emails";
import { cn } from "@/lib/cn";

interface TaskRowProps {
  task: CockpitTask;
  /** Provenance source line — "from <email subject>" style — lets
   *  the operator know where the task came from (brief §9). */
  sourceLabel?: string;
  onToggleDone?: (id: string, currentState: CockpitTask["state"]) => void;
}

const OWNER_COLORS: Record<string, string> = {
  ops: "text-op-accent",
  accounts: "text-op-green",
  commercial: "text-op-amber",
  fleet: "text-bucket-need-reply-now",
  "": "text-op-secondary",
};

/** One row in the bottom-strip tasks list. Tasks come from Sonnet's
 *  drafted reply (Phase 4.1) — every row carries an owner + (when
 *  inferable) a due date + the entity it's bound to + a provenance
 *  link back to the email that generated it. */
export function TaskRow({ task, sourceLabel, onToggleDone }: TaskRowProps) {
  const isDone = task.state === "done";
  const ownerKey = task.owner || "";
  return (
    <div
      data-testid="task-row"
      data-task-id={task.id}
      data-state={task.state}
      className={cn(
        "flex items-start gap-3 rounded border border-op-border bg-op-base px-3 py-2",
        isDone && "opacity-60",
      )}
    >
      <button
        type="button"
        aria-pressed={isDone}
        aria-label={isDone ? "Reopen task" : "Mark task done"}
        onClick={() => onToggleDone?.(task.id, task.state)}
        data-testid="task-row-toggle"
        className={cn(
          "mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border",
          isDone
            ? "border-op-green bg-op-green/20 text-op-green"
            : "border-op-border text-op-muted hover:border-op-accent",
        )}
      >
        {isDone ? "✓" : ""}
      </button>
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            "truncate text-sm text-op-primary",
            isDone && "line-through",
          )}
          title={task.title}
        >
          {task.title}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
          {task.owner ? (
            <span
              className={cn(
                "font-mono uppercase tracking-wider",
                OWNER_COLORS[ownerKey] ?? "text-op-secondary",
              )}
            >
              {task.owner}
            </span>
          ) : null}
          {task.due_at ? (
            <span className="font-mono text-op-secondary">
              due {formatDue(task.due_at)}
            </span>
          ) : null}
          <EntityRef entityRef={task.entity_ref} />
          {sourceLabel ? (
            <span className="truncate text-op-muted" title={sourceLabel}>
              {sourceLabel}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function EntityRef({ entityRef }: { entityRef: Record<string, string> }) {
  const tag = Object.entries(entityRef).find(([, v]) => v);
  if (!tag) return null;
  const [key, value] = tag;
  return (
    <span
      className="rounded border border-op-border bg-op-base px-1.5 py-0.5 font-mono text-[10px] text-op-secondary"
      title={`${key} ${value}`}
    >
      <span className="text-op-muted">{key}</span>
      <span className="ml-1 text-op-primary">{value}</span>
    </span>
  );
}

function formatDue(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}
